import { allEnumValues, FulfillmentMethod, I9nItemRefSortFields, I9nSyncScope, IntegrationSystem, PinataApiServer, UserRight, } from '@biteinc/enums';
import { AbstractModelSchema } from './abstract_model_schema.js';
export const integrationSchema = {
    displayName: 'integration',
    type: 'object',
    /**
     * @description This is a somewhat special schema.
     * Its fields are common to most integrations but not to all.
     * For example, the ones related to syncing sections are only applicable to POS integrations and
     * not to loyalty integrations. So if you are adding a field, make sure it's accounted for in
     * integration.js (both BE and FE).
     */
    fields: {
        ...AbstractModelSchema.fields,
        locationId: {
            type: 'mongoId',
            isHidden: true,
            isReadOnly: true,
        },
        system: {
            type: 'int',
            values: allEnumValues(IntegrationSystem),
            required: true,
            isPermanent: true,
            ui: 'dropdown',
            writeRight: UserRight.ManageI9n,
        },
        pinataApiServer: {
            type: 'int',
            displayName: 'Pinata API Server',
            ui: 'dropdown',
            values: allEnumValues(PinataApiServer),
            writeRight: UserRight.ManageI9n,
            isNullable: true,
            tooltip: {
                text: 'Sends requests to the specified Pinata instance instead of the integration',
                isWarning: true,
            },
        },
        vendorId: {
            type: 'mongoId',
            required: true,
            displayName: 'Vendor',
            ui: 'dropdown',
            writeRight: UserRight.ManageI9n,
        },
        loyaltyDiscountId: {
            type: 'keyString',
            displayName: 'Loyalty Discount on the POS',
            ui: 'dropdown',
            tooltip: {
                text: 'The discount used by the loyalty integration when rewards are sent to the POS. Can be set after first sync.',
            },
            writeRight: UserRight.ManageI9nLite,
        },
        diningOptions: {
            type: 'array',
            elementType: 'object',
            displayName: 'Dining Options',
            ui: 'accordion',
            uiSubtype: 'I9nDiningOptions',
            fields: {
                fulfillmentMethod: {
                    type: 'int',
                    values: allEnumValues(FulfillmentMethod),
                    required: true,
                    isHidden: true,
                    // "This is a bite FulfillmentMethod. Each integration can have it's own diningOptionFields defined which would get added as fields to this object."
                },
            },
            writeRight: UserRight.ManageI9nLite,
        },
        syncSections: {
            type: 'bool',
            defaultValue: true,
            displayName: 'Sync Sections',
            writeRight: UserRight.ManageI9n,
        },
        syncMenuStructures: {
            type: 'bool',
            displayName: 'Sync Menus',
            writeRight: UserRight.ManageI9n,
        },
        syncBadges: {
            type: 'bool',
            displayName: 'Sync Nutritional Tags and Allergens as Badges',
            writeRight: UserRight.ManageI9nLite,
            tooltip: {
                text: 'If enabled, nutritional tags and allergens will be synced from the POS as badges. Badges created in Bite Admin are preserved alongside synced badges and are not touched by sync.',
            },
        },
        preserveSectionItemOrder: {
            type: 'bool',
            displayName: 'Preserve order of items in sections',
            writeRight: UserRight.ManageI9n,
            conditions: [
                {
                    field: 'menuSectionItemSortPriority',
                    values: [undefined, []],
                },
            ],
        },
        menuSectionItemSortPriority: {
            type: 'array',
            elementType: 'object',
            displayName: 'Item Sort Priority',
            ui: 'accordion',
            uiSubtype: 'MenuSectionSortPriorities',
            writeRight: UserRight.ManageI9n,
            placeholder: 'Add a sort priority',
            conditions: [
                {
                    field: 'preserveSectionItemOrder',
                    values: [false, undefined],
                },
            ],
            isNullable: true,
            fields: {
                sortField: {
                    type: 'int',
                    values: allEnumValues(I9nItemRefSortFields),
                    required: true,
                    isHidden: true,
                },
                nameStartsWith: {
                    type: 'keyString',
                    displayName: 'Lookup Value',
                    required: true,
                    conditions: [
                        {
                            field: 'sortField',
                            values: allEnumValues(I9nItemRefSortFields).filter((sortField) => {
                                return sortField === I9nItemRefSortFields.NameStartsWith;
                            }),
                        },
                    ],
                },
            },
        },
        sectionSortPrioritiesApplyTo: {
            type: 'array',
            elementType: 'mongoId',
            ui: 'table',
            displayName: 'Apply "Item Sort Priority" To Sections',
            tooltip: {
                text: 'If not set, it applies to all sections. If set, the sort priorities will only apply to these sections.',
            },
            isNullable: true,
            conditions: [
                {
                    field: 'menuSectionItemSortPriority',
                    exists: true,
                },
            ],
        },
        preserveItemModGroupOrder: {
            type: 'bool',
            displayName: 'Preserve order of mod groups in items',
            writeRight: UserRight.ManageI9n,
        },
        preserveModGroupModOrder: {
            type: 'bool',
            displayName: 'Preserve order of mods in mod groups',
            writeRight: UserRight.ManageI9n,
            conditions: [
                {
                    field: 'modGroupModSortPriority',
                    values: [undefined, []],
                },
            ],
        },
        modGroupModSortPriority: {
            type: 'array',
            elementType: 'object',
            displayName: 'Mod Sort Priority',
            ui: 'accordion',
            uiSubtype: 'ModGroupSortPriorities',
            writeRight: UserRight.ManageI9n,
            placeholder: 'Add a sort priority',
            conditions: [
                {
                    field: 'preserveModGroupModOrder',
                    values: [false, undefined],
                },
            ],
            isNullable: true,
            fields: {
                sortField: {
                    type: 'int',
                    values: allEnumValues(I9nItemRefSortFields),
                    required: true,
                    isHidden: true,
                },
                nameStartsWith: {
                    type: 'codeString', // need to allow spaces
                    displayName: 'Lookup Value',
                    required: true,
                    conditions: [
                        {
                            field: 'sortField',
                            values: allEnumValues(I9nItemRefSortFields).filter((sortField) => {
                                return sortField === I9nItemRefSortFields.NameStartsWith;
                            }),
                        },
                    ],
                },
            },
        },
        maxSendOrderConcurrency: {
            type: 'int',
            min: 0,
            displayName: 'Max concurrency for sending orders',
            writeRight: UserRight.ManageI9nLite,
        },
        enableMultipleRewardRedemption: {
            type: 'bool',
            displayName: 'Enable multiple reward redemptions',
            writeRight: UserRight.ManageI9n,
            defaultValue: true,
        },
        useAsSecondaryPayment: {
            type: 'bool',
            displayName: 'Use as Secondary Payment',
            writeRight: UserRight.ManageI9n,
            tooltip: {
                text: 'The gift card button would appear right next to the credit card button.',
            },
        },
        skipNonSyncOperations: {
            type: 'bool',
            displayName: 'Skip Non-Sync Operations',
            writeRight: UserRight.ManageI9n,
        },
        disableSync: {
            type: 'bool',
            displayName: 'Disable Automatic Syncing',
            writeRight: UserRight.ManageI9n,
        },
        automaticSyncScope: {
            type: 'stringEnum',
            enum: I9nSyncScope,
            displayName: 'Automatic Sync Scope',
            writeRight: UserRight.ManageI9n,
            defaultValue: I9nSyncScope.FullSync,
        },
        disable: {
            type: 'bool',
            displayName: 'Disable Temporarily',
            writeRight: UserRight.ManageI9n,
        },
        notes: {
            type: 'longString',
            writeRight: UserRight.ManageI9n,
        },
        notifyUserOfOrderStatusUpdates: {
            type: 'bool',
            displayName: 'Notify User of Order Status Updates',
            tooltip: {
                text: 'If the user provides contact information, send a message to the user when we detect that status of the order has changed. Twilio integration must be added to this channel for SMS to be sent. Please make sure "Enable order status notifications" flag is checked under each dining option that should receive notifications.',
            },
            writeRight: UserRight.ManageI9n,
        },
        useWebhooksForOrderStatuses: {
            type: 'bool',
            displayName: 'Use Webhooks For Order Statuses',
            tooltip: {
                text: 'This integration supports polling and webhooks to update order statuses. Check this box to use webhooks. If unchecked, webhooks will be ignored and if notifications are turned on, interval polling will be used.',
            },
            writeRight: UserRight.ManageI9n,
        },
        sendOrderStatusCheckup: {
            type: 'bool',
            displayName: 'Send Order Status Checkup',
            tooltip: {
                text: 'After 30 minutes, fetch the status of the order in case we missed the "Order Complete" webhook.',
            },
            writeRight: UserRight.ManageI9n,
            conditions: [
                {
                    field: 'useWebhooksForOrderStatuses',
                    values: [true],
                },
            ],
            defaultValue: true,
        },
        label: {
            type: 'shortString',
            displayName: 'Label',
            tooltip: {
                text: 'If the guest has to choose between multiple integrations, this label will be shown.',
            },
            writeRight: UserRight.ManageI9nLite,
            canHaveLocalizationOverrides: true,
        },
        icons: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            displayName: 'Icon',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            keepAspectRatio: 'contain',
            tooltip: {
                text: 'If the guest has to choose between multiple integrations, this icon will be shown.',
            },
            size: {
                width: 300,
                height: 200,
            },
            jpgSize: {
                width: 300,
                height: 200,
            },
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageI9nLite,
        },
        syncInfo: {
            type: 'object',
            isPermanent: true,
            isReadOnly: true,
            isHidden: true,
            fields: {
                useForceOnNextSync: {
                    type: 'bool',
                },
                automaticSyncIncidentLastError: {
                    type: 'object',
                    fields: {
                        occurredAt: {
                            type: 'timestamp',
                        },
                        description: {
                            type: 'keyString',
                            required: true,
                        },
                        stack: {
                            type: 'keyString',
                        },
                        debugMessage: {
                            type: 'keyString',
                        },
                        mdCode: {
                            type: 'int',
                        },
                    },
                },
                syncedAt: {
                    type: 'timestamp',
                },
                checksum: {
                    type: 'keyString',
                },
                summary: {
                    type: 'object',
                    fields: {
                        summaries: {
                            type: 'array',
                            elementType: 'shortString',
                        },
                        warnings: {
                            type: 'array',
                            elementType: 'shortString',
                        },
                    },
                },
                syncStartedAt: {
                    type: 'timestamp',
                },
                seq: {
                    type: 'int',
                },
            },
        },
        syncedData: {
            type: 'object',
            isReadOnly: true,
            isFreeform: true,
        },
        syncPriority: {
            type: 'int',
            displayName: 'Sync Priority',
            writeRight: UserRight.ManageI9n,
            required: true,
            tooltip: {
                text: 'A higher priority will overwrite menu supplements with a lower priority.',
            },
        },
    },
};
//# sourceMappingURL=integration_schema.js.map